import React from "react";
import { withStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import {
  makeSpotifyAPIRequest,
  getAlbumArtURLFromImages,
} from "../api_utilities/spotify";
import AlbumAccordion from "./AlbumAccordion";
import Typography from "@material-ui/core/Typography";
import { Grid } from "@material-ui/core";
import {
  isIDSpotifyInInfos,
  getTagsFromBackendAlbum,
  getTrackRatingFromBackendAlbum,
  makeBackendAPIRequest,
  backendBaseURL,
  linkTracks,
} from "../api_utilities/backend";
import Track from "./Track";
import List from "@material-ui/core/List";
import CircularProgress from "@material-ui/core/CircularProgress";

const useStyles = (theme) => ({
  root: {
    padding: theme.spacing(2),
    height: "100%",
  },
  resultGroup: {
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    width: "100%",
    boxShadow: "0px 0px 5px 1px rgba(0,0,0,0.24)",
  },
  spaceBelow: {
    marginBottom: theme.spacing(2),
  },
});

class SearchPane extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      albums: [],
      tracks: [],
      albumsLoading: false,
      tracksLoading: false,
    };
  }

  componentDidMount() {
    this.searchSpotify();
  }

  componentDidUpdate(prevProps) {
    const { searchString } = this.props;

    if (searchString !== prevProps.searchString) {
      this.searchSpotify();
    }
  }

  searchSpotify = () => {
    const { searchString } = this.props;

    if (searchString === "") {
      this.setState({ albums: [], tracks: [] });
      return;
    }

    this.setState({ albumsLoading: true, tracksLoading: true });

    const url = new URL("https://api.spotify.com/v1/search");
    url.searchParams.append("type", "album,track");
    url.searchParams.append("q", searchString);
    url.searchParams.append("limit", 3);

    makeSpotifyAPIRequest({
      url: url,
      method: "GET",
      onSuccessCallback: (data) => {
        this.getCompleteAlbumsFromSimplifiedSpotifyAlbums(
          data.albums.items.map((album) => album.id)
        );
        this.getCompleteTracksFromSpotifyTracks(data.tracks.items);
      },
      onNoContentCallback: () => {},
    });
  };

  getCompleteTracksFromSpotifyTracks = (spotifyTracks) => {
    if (spotifyTracks.length === 0) {
      this.setState({ tracksLoading: false, tracks: [] });

      return;
    }

    const tracksPayload = spotifyTracks.map((track) => {
      return {
        id_spotify: track.id,
        song_name: track.name,
        duration_ms: track.duration_ms,
        track_number: track.track_number,
        album: {
          id_spotify: track.album.id,
          name: track.album.name,
          artists: track.album.artists.map((artist) => artist.name).join(", "),
          release_year: new Date(track.album.release_date).getFullYear(),
          total_tracks: track.album.total_tracks,
        },
      };
    });

    const url = new URL(backendBaseURL + "/song/get_songs_and_tags");

    makeBackendAPIRequest({
      url: url,
      method: "POST",
      body: { tracks: JSON.stringify(tracksPayload) },
      onSuccessCallback: (response) => {
        const backendTracksAndTags = response.songs_and_tags;
        this.setState({
          tracksLoading: false,
          tracks: spotifyTracks.map((spotifyTrack, i) => {
            const backendTrackAndTags =
              backendTracksAndTags.find(
                (songAndTag) => songAndTag.id_spotify == spotifyTrack.id
              ) ?? {};

            return {
              trackID: spotifyTrack.id,
              trackIndex: i,
              albumID: spotifyTrack.album.id,
              albumTotalTracks: spotifyTrack.album.total_tracks,
              artistName: spotifyTrack.artists
                .map((artist) => artist.name)
                .join(", "),
              trackName: spotifyTrack.name,
              trackNumber: spotifyTrack.track_number,
              trackLength: spotifyTrack.duration_ms,
              rating: backendTrackAndTags.rating ?? "",
              tags: backendTrackAndTags.tags ?? [],
              albumName: spotifyTrack.album.name,
              releaseYear: new Date(
                spotifyTrack.album.release_date
              ).getFullYear(),
              albumArtURL: getAlbumArtURLFromImages(spotifyTrack.album.images),
              albumArtists: spotifyTrack.album.artists
                .map((artist) => artist.name)
                .join(", "),
            };
          }),
        });
      },
      onFailureCallback: () => {},
    });
  };

  getCompleteAlbumsFromSimplifiedSpotifyAlbums = (albumIDs) => {
    if (albumIDs.length === 0) {
      this.setState({ albumsLoading: false, albums: [] });

      return;
    }

    const url = new URL("https://api.spotify.com/v1/albums");
    url.searchParams.append("ids", albumIDs.join(","));
    makeSpotifyAPIRequest({
      url: url,
      method: "GET",
      onSuccessCallback: (data) => {
        this.getCompleteAlbumsFromSpotifyAlbums(data.albums);
      },
      onNoContentCallback: () => {},
    });
  };

  getCompleteAlbumsFromSpotifyAlbums = (spotifyAlbums) => {
    const url = new URL(backendBaseURL + "/album/get_albums");
    const albumsPayload = spotifyAlbums.map((album) => {
      return {
        id_spotify: album.id,
        name: album.name,
        artists: album.artists.map((artist) => artist.name).join(", "),
        release_year: new Date(album.release_date).getFullYear(),
        total_tracks: album.total_tracks,
        art_url: getAlbumArtURLFromImages(album.images),
      };
    });

    makeBackendAPIRequest({
      url: url,
      method: "POST",
      body: { albums: JSON.stringify(albumsPayload) },
      onSuccessCallback: (response) => {
        const backendAlbums = response.albums;
        this.setState({
          albumsLoading: false,
          albums: spotifyAlbums.map((spotifyAlbum, i) => {
            const backendAlbum =
              backendAlbums.find((album) =>
                isIDSpotifyInInfos(album.album_infos, spotifyAlbum.id)
              ) ?? {};

            const link_objects = [];

            const complete_album = {
              albumIndex: i,
              albumID: spotifyAlbum.id,
              albumName: spotifyAlbum.name,
              albumArtists: spotifyAlbum.artists
                .map((artist) => artist.name)
                .join(", "),
              albumTotalTracksRated:
                backendAlbum.songs != null ? backendAlbum.songs.length : 0,
              albumTotalTracks: spotifyAlbum.tracks.total,
              albumRating: backendAlbum.rating ?? 0,
              averageScore: backendAlbum.average_score ?? 0,
              releaseYear: new Date(spotifyAlbum.release_date).getFullYear(),
              albumArtURL: getAlbumArtURLFromImages(spotifyAlbum.images),
              tracks: spotifyAlbum.tracks.items.map((track, i) => {
                const { rating, rating_links } = getTrackRatingFromBackendAlbum(
                  track,
                  backendAlbum
                );
                const { tags, tag_links } = getTagsFromBackendAlbum(
                  track,
                  backendAlbum
                );
                link_objects.push(...rating_links, ...tag_links);

                return {
                  index: i,
                  id: track.id,
                  name: track.name,
                  artistName: track.artists
                    .map((artist) => artist.name)
                    .join(", "),
                  trackLength: track.duration_ms,
                  trackNumber: track.track_number,
                  rating: rating,
                  tags: tags,
                };
              }),
            };

            linkTracks(link_objects);

            return complete_album;
          }),
        });
      },
      onFailureCallback: () => {},
    });
  };

  onTagsUpdatedOptimistic = (_, trackIndex, newValue) => {
    const { albums, tracks } = this.state;

    tracks[trackIndex].tags = newValue;

    this.setState({ tracks: tracks });

    const albumID = tracks[trackIndex].albumID;
    const trackID = tracks[trackIndex].trackID;

    for (const album of albums) {
      if (album.albumID === albumID) {
        for (const track of album.tracks) {
          if (track.id === trackID) {
            track.tags = newValue;
            this.setState({ albums: albums });

            return;
          }
        }
      }
    }
  };

  onAlbumTagsUpdatedOptimistic = (albumIndex, trackIndex, newValue) => {
    const { albums, tracks } = this.state;

    albums[albumIndex].tracks[trackIndex].tags = newValue;
    this.setState({ albums: albums });

    for (const track of tracks) {
      if (track.trackID === albums[albumIndex].tracks[trackIndex].id) {
        track.tags = newValue;
        this.setState({ tracks: tracks });

        return;
      }
    }
  };

  onTrackRatedOptimistic = (_, trackIndex, newValue) => {
    const { albums, tracks } = this.state;

    tracks[trackIndex].rating = newValue;

    this.setState({ tracks: tracks });

    const albumID = tracks[trackIndex].albumID;
    const trackID = tracks[trackIndex].trackID;

    for (const album of albums) {
      if (album.albumID === albumID) {
        for (const track of album.tracks) {
          if (track.id === trackID) {
            track.rating = newValue;
            this.setState({ albums: albums });

            return;
          }
        }
      }
    }
  };

  onAlbumTrackRatedOptimistic = (albumIndex, trackIndex, newValue) => {
    const { albums, tracks } = this.state;

    albums[albumIndex].tracks[trackIndex].rating = newValue;
    this.setState({ albums: albums });

    for (const track of tracks) {
      if (track.trackID === albums[albumIndex].tracks[trackIndex].id) {
        track.rating = newValue;
        this.setState({ tracks: tracks });

        return;
      }
    }
  };

  onTrackRatedCallback = (albumID) => {
    const { albums } = this.state;

    const url = new URL(backendBaseURL + "/album/get_album");
    url.searchParams.append("id_spotify", albumID);

    makeBackendAPIRequest({
      url: url,
      method: "GET",
      onSuccessCallback: (backendAlbum) => {
        for (const album of albums) {
          if (isIDSpotifyInInfos(backendAlbum.album_infos, album.id)) {
            album.albumTotalTracksRated =
              backendAlbum.songs != null ? backendAlbum.songs.length : 0;
            album.albumRating = backendAlbum.rating;
            album.averageScore = backendAlbum.average_score;

            this.setState({ albums: albums });
            return;
          }
        }
      },
      onFailureCallback: () => {},
    });
  };

  onAlbumTrackRatedCallback = (albumID, albumIndex) => {
    const url = new URL(backendBaseURL + "/album/get_album");
    url.searchParams.append("id_spotify", albumID);

    makeBackendAPIRequest({
      url: url,
      method: "GET",
      onSuccessCallback: (backendAlbum) => {
        this.setState((prevState) => {
          const albums = prevState.albums;
          const album = albums[albumIndex];
          album.albumTotalTracksRated =
            backendAlbum.songs != null ? backendAlbum.songs.length : 0;
          album.albumRating = backendAlbum.rating;
          album.averageScore = backendAlbum.average_score;

          return { albums: albums };
        });
      },
      onFailureCallback: () => {},
    });
  };

  render() {
    const { classes, searchString, store } = this.props;
    const { albums, tracks, albumsLoading, tracksLoading } = this.state;

    return (
      <Card className={classes.root}>
        <Grid container spacing={3}>
          <Grid
            item
            container
            xs={12}
            justify={"flex-end"}
            className={classes.spaceBelow}
          >
            <Typography variant="overline">Search Results</Typography>
          </Grid>
          {albumsLoading && tracksLoading && searchString !== "" ? (
            <Grid item container xs={12} justify={"center"}>
              <CircularProgress />
            </Grid>
          ) : (
            <div />
          )}
          {tracks.length > 0 ? (
            <Card className={classes.resultGroup}>
              <Grid
                item
                container
                xs={12}
                justify={"flex-end"}
                className={classes.spaceBelow}
              >
                <Typography variant="overline">Tracks</Typography>
              </Grid>
              <List>
                {tracks.map((track, i) => (
                  <Track
                    key={track.trackID}
                    trackAndAlbumInfo={track}
                    onTrackRated={{
                      callback: this.onTrackRatedCallback,
                      optimistic: this.onTrackRatedOptimistic,
                    }}
                    onTagsUpdatedOptimistic={this.onTagsUpdatedOptimistic}
                    trackIDsAfter={tracks
                      .slice(i + 1)
                      .map((track) => track.trackID)}
                    store={store}
                  />
                ))}
              </List>
            </Card>
          ) : (
            <div />
          )}
          {albums.length > 0 ? (
            <Card className={classes.resultGroup}>
              <Grid
                item
                container
                xs={12}
                justify={"flex-end"}
                className={classes.spaceBelow}
              >
                <Typography variant="overline">Albums</Typography>
              </Grid>
              {albums.map((album) => (
                <Grid
                  item
                  xs={12}
                  key={album.albumID}
                  className={classes.spaceBelow}
                >
                  <AlbumAccordion
                    trackAndAlbumInfo={album}
                    hasLoadedTracks={true}
                    onTrackRated={{
                      callback: this.onAlbumTrackRatedCallback,
                      optimistic: this.onAlbumTrackRatedOptimistic,
                    }}
                    onTagsUpdatedOptimistic={this.onAlbumTagsUpdatedOptimistic}
                    onOpen={() => {}}
                    store={store}
                  />
                </Grid>
              ))}
            </Card>
          ) : (
            <div />
          )}
        </Grid>
      </Card>
    );
  }
}

export default withStyles(useStyles)(SearchPane);
